import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Eye, Filter, Loader2, CreditCard, Wallet } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '@/services/api';

interface Transaction {
  id: string | number;
  type: 'wallet_topup' | 'quote_payment';
  date: string;
  amount: number;
  description: string;
  source: string;
  status: 'completed' | 'paid';
  quoteId?: number;
  serviceName?: string;
}

const BillingActivityPage = () => {
  const [dateRange, setDateRange] = useState('last-30');
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState([
    { label: 'TOTAL PAID', value: '£0.00', subtext: 'ALL TRANSACTIONS' },
    { label: 'WALLET TOP-UPS', value: '£0.00', subtext: '0 TRANSACTIONS' },
    { label: 'QUOTE PAYMENTS', value: '£0.00', subtext: '0 SERVICES' },
    { label: 'TRANSACTION COUNT', value: '0', subtext: 'THIS PERIOD' },
  ]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 5;

  useEffect(() => {
    loadPaymentHistory();
  }, [dateRange]);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await apiService.request(`/customer/payment-history?period=${dateRange}`, 'GET');
      if (response.success && response.data) {
        setTransactions(response.data.transactions || []);
        const transactionStats = response.data.stats || {};
        setStats([
          { label: 'TOTAL PAID', value: `£${transactionStats.totalAmount?.toFixed(2) || '0.00'}`, subtext: 'ALL TRANSACTIONS' },
          { label: 'WALLET TOP-UPS', value: `£${transactionStats.topupsAmount?.toFixed(2) || '0.00'}`, subtext: `${transactionStats.topupCount || 0} TRANSACTIONS` },
          { label: 'QUOTE PAYMENTS', value: `£${transactionStats.paymentsAmount?.toFixed(2) || '0.00'}`, subtext: `${transactionStats.paymentCount || 0} SERVICES` },
          { label: 'TRANSACTION COUNT', value: String(transactionStats.topupCount + transactionStats.paymentCount || 0), subtext: 'THIS PERIOD' },
        ]);
      }
    } catch (error) {
      console.error('Failed to load payment history:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getTransactionIcon = (type: string) => {
    return type === 'wallet_topup' ? (
      <Wallet className="h-5 w-5" />
    ) : (
      <CreditCard className="h-5 w-5" />
    );
  };

  const getTransactionTypeLabel = (type: string) => {
    return type === 'wallet_topup' ? 'WALLET TOP-UP' : 'QUOTE PAYMENT';
  };

  const handleViewTransaction = (transaction: Transaction) => {
    // Show transaction details modal
    const details = `
Transaction ID: ${transaction.id}
Type: ${getTransactionTypeLabel(transaction.type)}
Amount: £${transaction.amount.toFixed(2)}
Date: ${new Date(transaction.date).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
Source: ${transaction.source}
Status: ${transaction.status.toUpperCase()}
${transaction.serviceName ? `Service: ${transaction.serviceName}` : ''}
${transaction.quoteId ? `Quote ID: ${transaction.quoteId}` : ''}
Description: ${transaction.description}
    `;
    alert(details);
  };

  const handleDownloadTransaction = (transaction: Transaction) => {
    // Download transaction as CSV
    const csvContent = `"Transaction ID","Type","Amount","Date","Source","Status","Description"
"${transaction.id}","${getTransactionTypeLabel(transaction.type)}","£${transaction.amount.toFixed(2)}","${new Date(transaction.date).toLocaleDateString('en-GB')}","${transaction.source}","${transaction.status.toUpperCase()}","${transaction.description}"`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaction-${transaction.id}-${new Date().getTime()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportRecords = () => {
    if (transactions.length === 0) return;
    const header = '"Transaction ID","Type","Amount","Date","Source","Status","Description"';
    const rows = transactions.map((t) =>
      `"${t.id}","${getTransactionTypeLabel(t.type)}","£${t.amount.toFixed(2)}","${new Date(t.date).toLocaleDateString('en-GB')}","${t.source}","${t.status.toUpperCase()}","${t.description}"`
    );
    const csvContent = [header, ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-activity-${dateRange}-${new Date().getTime()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Billing Activity | Virewire</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Your Payment History</h1>
          <p className="text-gray-600 mt-2">
            View all your wallet top-ups and quote payments. Track your financial transactions
            and payment records in one place.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <Card key={idx} className="border-1">
              <CardContent className="p-6">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-600 mt-2">• {stat.subtext}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <Filter className="h-5 w-5 text-gray-600" />
            <select 
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200"
            >
              <option value="last-30">Last 30 Days</option>
              <option value="last-90">Last 90 Days</option>
              <option value="last-year">Last Year</option>
            </select>
          </div>
          <Button
            className="bg-green-700 hover:bg-green-800 text-white"
            onClick={handleExportRecords}
            disabled={transactions.length === 0}
          >
            <Download className="h-5 w-5 mr-2" />
            Export Records
          </Button>
        </div>

        {/* Transactions List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
              <p className="ml-2 text-gray-600">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <Card className="border-1">
              <CardContent className="p-12 text-center">
                <p className="text-gray-600">No transactions found for the selected period</p>
              </CardContent>
            </Card>
          ) : (
            paginatedTransactions.map((transaction) => (
              <Card key={`${transaction.type}-${transaction.id}`} className="border-1 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Icon */}
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          transaction.type === 'wallet_topup'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {getTransactionIcon(transaction.type)}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              transaction.type === 'wallet_topup'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {getTransactionTypeLabel(transaction.type)}
                          </span>
                          <p className="text-sm font-bold text-gray-600">
                            ID: {transaction.id}
                          </p>
                        </div>

                        <h3 className="font-bold text-gray-900 text-lg mb-1">
                          {transaction.description}
                        </h3>
                        <p className="text-sm text-gray-600">
                          📍 Source: {transaction.source}
                        </p>
                        {transaction.serviceName && (
                          <p className="text-sm text-gray-600">
                            🔧 Service: {transaction.serviceName}
                          </p>
                        )}
                        {transaction.quoteId && (
                          <p className="text-sm text-gray-600">
                            📋 Quote #: {transaction.quoteId}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          📅 {new Date(transaction.date).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="text-right">
                        <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                          Amount
                        </p>
                        <p className="font-semibold text-gray-900 text-lg">
                          £{transaction.amount.toFixed(2)}
                        </p>
                        <p className={`text-xs font-semibold mt-1 ${
                          transaction.status === 'completed' || transaction.status === 'paid'
                            ? 'text-green-600'
                            : 'text-yellow-600'
                        }`}>
                          ✓ {transaction.status.toUpperCase()}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleViewTransaction(transaction)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View transaction details"
                        >
                          <Eye className="h-5 w-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDownloadTransaction(transaction)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Download transaction as CSV"
                        >
                          <Download className="h-5 w-5 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Showing {transactions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, transactions.length)} of{' '}
            {transactions.length} transactions
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${
                    currentPage === page
                      ? 'bg-green-700 text-white'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BillingActivityPage;
